// Base URLs from environment variables
const API_URL = process.env.REACT_APP_API_URL || '';

export interface FileInfo {
  id: string;
  name: string;
  title: string;
  description?: string;
  path: string;
  type: 'file' | 'document' | 'code';
  category?: string;
  tags?: string[];
  fileSize?: number;
  mimeType?: string;
  uploadedAt?: string;
  analysisHistory?: any[]; // 分析履歴を追加
}

export interface FileSummary {
  file_id: string;
  summary: string;
}

export interface FileSearchFilters {
  categories?: string[];
  tags?: string[];
  fileTypes?: string[];
}

export interface FileMetadata {
  categories: string[];
  tags: string[];
  fileTypes: string[];
}

export interface MultiFileSummaryRequest {
  fileIds: string[];
  includeIndividual?: boolean;
  includeCombined?: boolean;
}

export interface MultiFileSummaryResponse {
  individualSummaries?: { [fileId: string]: string };
  combinedSummary?: string;
  fileInfo?: { [fileId: string]: FileInfo };
}

class FileService {
  /**
   * Get access token from sessionStorage
   */
  private getAccessToken(): string | null {
    return sessionStorage.getItem('access_token');
  }

  /**
   * Get headers with Authorization token
   * Cookie認証廃止により、Authorization headerのみを使用
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get list of available files for selection with optional filters
   */
  async getFilesForSelection(filters?: FileSearchFilters): Promise<FileInfo[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.categories?.length) {
        params.set('categories', filters.categories.join(','));
      }
      if (filters?.tags?.length) {
        params.set('tags', filters.tags.join(','));
      }
      if (filters?.fileTypes?.length) {
        params.set('file_types', filters.fileTypes.join(','));
      }

      const queryString = params.toString();
      const url = queryString ? `${API_URL}/files/list?${queryString}` : `${API_URL}/files/list`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }

      const files: FileInfo[] = await response.json();

      // Map the file type based on mimeType if needed
      return files.map(file => ({
        ...file,
        type: this.getFileTypeFromMimeType(file.mimeType || 'application/octet-stream') as 'file' | 'document' | 'code',
      }));
    } catch (error) {
      console.error('Failed to fetch files for selection:', error);
      throw error;
    }
  }

  /**
   * Get summary for a specific file
   */
  async getFileSummary(fileId: string, deep = false): Promise<string> {
    try {
      const url = `${API_URL}/files/${fileId}/summary${deep ? '?deep=true' : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch file summary');
      }

      const result: FileSummary = await response.json();
      return result.summary;
    } catch (error) {
      console.error(`Failed to fetch summary for file ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Get summaries for multiple files
   * This is a convenience method that calls getFileSummary for each file
   */
  async getMultipleFileSummaries(
    fileIds: string[],
    includeIndividual = true,
    includeCombined = true,
    useDeepAnalysis = false
  ): Promise<MultiFileSummaryResponse> {
    try {
      const result: MultiFileSummaryResponse = {};

      // Get individual summaries
      if (includeIndividual) {
        const summaryPromises = fileIds.map(async (fileId) => {
          try {
            const summary = await this.getFileSummary(fileId, useDeepAnalysis);
            return { fileId, summary };
          } catch (error) {
            console.error(`Failed to get summary for file ${fileId}:`, error);
            return { fileId, summary: 'Summary not available' };
          }
        });

        const summaries = await Promise.all(summaryPromises);
        result.individualSummaries = {};
        summaries.forEach(({ fileId, summary }) => {
          if (result.individualSummaries) {
            result.individualSummaries[fileId] = summary;
          }
        });
      }

      // Create combined summary if requested
      if (includeCombined && result.individualSummaries) {
        const allSummaries = Object.values(result.individualSummaries).filter(s => s !== 'Summary not available');
        if (allSummaries.length > 0) {
          result.combinedSummary = this.createCombinedSummary(allSummaries, fileIds, useDeepAnalysis);
        }
      }

      return result;
    } catch (error) {
      console.error('Failed to get multiple file summaries:', error);
      throw error;
    }
  }

  /**
   * Create a combined summary from individual summaries
   */
  private createCombinedSummary(summaries: string[], fileIds: string[], isDeepAnalysis = false): string {
    if (summaries.length === 0) return '';

    if (summaries.length === 1) {
      return summaries[0];
    }

    // Create a structured combined summary
    let combined = `## 全体的な概要\n`;
    combined += `選択されたファイル（${fileIds.length}件）の内容を統合したサマリーです。\n\n`;

    // Add individual summaries with headers
    summaries.forEach((summary, index) => {
      combined += `### ファイル ${index + 1}\n`;
      combined += `${summary}\n\n`;
    });

    // Only add technical insights for basic summaries, not for deep analysis
    if (!isDeepAnalysis) {
      combined += `### 技術的知見とポイント\n`;
      combined += this.generateTechnicalInsights(summaries);
    }

    return combined;
  }

  /**
   * Generate technical insights from summaries
   */
  private generateTechnicalInsights(summaries: string[]): string {
    // Common technical keywords and patterns to look for
    const technicalKeywords = {
      materials: ['材料', '素材', 'アルミ', '鋼', '樹脂', '金属', '合金'],
      environment: ['高温', '低温', '湿度', '腐食', '環境', '温度', '圧力'],
      failure: ['不具合', '故障', '破損', '亀裂', '劣化', '漏れ', '異常'],
      design: ['設計', '仕様', '基準', '規格', '標準', '要求'],
      testing: ['試験', 'テスト', '検証', '評価', '測定', '実験'],
      process: ['工程', 'プロセス', '製造', '品質', '管理'],
      countermeasures: ['対策', '改善', '防止', '予防', '再発防止']
    };

    let insights = '';
    const allText = summaries.join(' ').toLowerCase();

    // Analyze patterns and generate insights
    const foundCategories = [];

    if (this.hasKeywords(allText, technicalKeywords.failure)) {
      foundCategories.push('故障分析');
      insights += `**🔍 故障分析の重要性**\n`;
      insights += `- 根本原因の特定と対策の体系化が必要\n`;
      insights += `- 同様の不具合の横展開防止策を検討\n\n`;
    }

    if (this.hasKeywords(allText, technicalKeywords.environment)) {
      foundCategories.push('環境要因');
      insights += `**🌡️ 環境要因への対応**\n`;
      insights += `- 使用環境条件の十分な検討と設計への反映\n`;
      insights += `- グローバル市場の多様な環境条件を考慮した設計基準の見直し\n\n`;
    }

    if (this.hasKeywords(allText, technicalKeywords.design)) {
      foundCategories.push('設計改善');
      insights += `**📐 設計・開発プロセスの改善**\n`;
      insights += `- 設計基準の見直しと実使用条件との整合性確保\n`;
      insights += `- 設計検証プロセスの強化と評価項目の追加\n\n`;
    }

    if (this.hasKeywords(allText, technicalKeywords.testing)) {
      foundCategories.push('試験・評価');
      insights += `**🧪 試験・評価体制の強化**\n`;
      insights += `- 実使用条件を模擬した試験方法の確立\n`;
      insights += `- 試験設備の導入と評価体制の整備\n\n`;
    }

    if (this.hasKeywords(allText, technicalKeywords.materials)) {
      foundCategories.push('材料選定');
      insights += `**🔬 材料・部品選定の最適化**\n`;
      insights += `- 使用環境に適した材料特性の評価と選定\n`;
      insights += `- 材料の長期信頼性と耐久性の検証\n\n`;
    }

    // Add general recommendations if no specific patterns found
    if (foundCategories.length === 0) {
      insights += `**📋 一般的な技術的考慮事項**\n`;
      insights += `- 文書化された知見の体系的な整理と共有\n`;
      insights += `- 類似事例との比較分析による知見の拡充\n\n`;
    }

    // Add common recommendations
    insights += `**💡 推奨アクション**\n`;
    insights += `- 関連技術者・部門との情報共有と議論\n`;
    insights += `- 類似製品・プロジェクトへの横展開の検討\n`;
    insights += `- 継続的な改善活動への組み込み\n`;

    return insights;
  }

  /**
   * Check if text contains keywords from a category
   */
  private hasKeywords(text: string, keywords: string[]): boolean {
    return keywords.some(keyword => text.includes(keyword));
  }

  /**
   * Determine file type from MIME type
   */
  private getFileTypeFromMimeType(mimeType: string): string {
    if (mimeType.startsWith('text/') || mimeType === 'application/json') {
      return 'code';
    }
    if (mimeType.includes('pdf') || mimeType.includes('word') || mimeType.includes('document')) {
      return 'document';
    }
    return 'file';
  }

  /**
   * Search files by name or content with optional filters
   */
  async searchFiles(query: string, limit = 10, filters?: FileSearchFilters): Promise<FileInfo[]> {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(limit),
      });
      if (filters?.categories?.length) {
        params.set('categories', filters.categories.join(','));
      }
      if (filters?.tags?.length) {
        params.set('tags', filters.tags.join(','));
      }
      if (filters?.fileTypes?.length) {
        params.set('file_types', filters.fileTypes.join(','));
      }

      const response = await fetch(
        `${API_URL}/files/search?${params}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        // If search fails, fall back to getting all files and filtering locally
        console.warn('File search API not available, using local filtering');
        const allFiles = await this.getFilesForSelection(filters);
        return allFiles.filter(file =>
          file.name.toLowerCase().includes(query.toLowerCase()) ||
          file.title.toLowerCase().includes(query.toLowerCase()) ||
          (file.description && file.description.toLowerCase().includes(query.toLowerCase()))
        ).slice(0, limit);
      }

      const searchResults = await response.json();
      return searchResults.map((result: any) => ({
        id: result.id,
        name: result.file_name || result.fileName,
        title: result.title,
        description: result.metadata?.summary || '',
        path: result.file_path || '',
        type: this.getFileTypeFromMimeType(result.metadata?.mimeType || 'application/octet-stream') as 'file' | 'document' | 'code',
        category: result.category,
        tags: result.tags || [],
      }));
    } catch (error) {
      console.error('Failed to search files:', error);
      // Return empty array instead of throwing to allow graceful degradation
      return [];
    }
  }

  /**
   * Get available metadata values for filter UI (categories, tags, file types)
   */
  async getFileMetadata(): Promise<FileMetadata> {
    try {
      const response = await fetch(`${API_URL}/files/metadata`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch file metadata');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch file metadata:', error);
      return { categories: [], tags: [], fileTypes: [] };
    }
  }
}

export const fileService = new FileService();
export default fileService;
